import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { FMS_READ_SCOPE } from '#/api/fmsMgmt/fmsReadScope';
import { fetchPaymentRequestDetail } from '#/api/fmsMgmt/paymentRequest';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import PageShell from '#/modules/infra/layout/PageShell';
import { paymentRequestDetailQueryKey } from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

import PaymentRequestCommandBar from '../components/PaymentRequestCommandBar';
import PaymentRequestDetailTabs from '../components/PaymentRequestDetailTabs';
import PaymentRequestMainInfoCard from '../components/PaymentRequestMainInfoCard';
import { getPaymentRequestApiErrorMessage } from '../helper/apiError';

const MODE_CONFIG = {
  personal: {
    backTo: ROUTES.personalCenterClaimsPanel,
    // 關聯憑證一律走請款單衍生路由（be doc 261019），不開獨立憑證詳情
    getVoucherDetailPath: ROUTES.personalCenterClaimVoucherDetail,
  },
  finance: {
    backTo: ROUTES.fmsPaymentRequest,
    getVoucherDetailPath: ROUTES.fmsPaymentRequestVoucherDetail,
  },
};

/**
 * 請款單詳情。personal＝個人中心（填單人操作）；finance＝財務管理（會計審核）。
 */
const PaymentRequestDetailPage = ({ mode = 'personal' }) => {
  const { id } = useParams();
  const config = MODE_CONFIG[mode];
  // 個人中心走 `/self`（`.self`）；財務管理走原路由（`.read`）
  const scope = mode === 'personal' ? FMS_READ_SCOPE.SELF : FMS_READ_SCOPE.ALL;
  const detailQuery = useQuery({
    queryKey: paymentRequestDetailQueryKey(id, scope),
    queryFn: () => fetchPaymentRequestDetail(id, { scope }),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (!detailQuery.isError) {
      return;
    }
    toast.error(getPaymentRequestApiErrorMessage(detailQuery.error, '載入請款單失敗'));
  }, [detailQuery.isError, detailQuery.error]);

  const detail = detailQuery.data?.data;

  return (
    <PageShell variant="detail">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div
            className={cn(
              'sticky top-0 z-20 -mx-1 flex flex-wrap items-center gap-2',
              'border-b border-slate-200 bg-canvas px-1 pb-3 pt-1',
            )}
          >
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">請款單詳情</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={config.backTo}>返回列表</Link>
            </Button>
          </div>

          {detailQuery.isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {detailQuery.isError ? (
            <p className="text-sm text-destructive">
              {getPaymentRequestApiErrorMessage(detailQuery.error, '載入請款單失敗')}
            </p>
          ) : null}

          {detail ? (
            <div className="space-y-4">
              <PaymentRequestMainInfoCard
                detail={detail}
                actions={(
                  <PaymentRequestCommandBar
                    key={`${detail.id}-${detail.record?.updatedAt ?? ''}`}
                    detail={detail}
                    mode={mode}
                  />
                )}
              />
              <PaymentRequestDetailTabs
                detail={detail}
                getVoucherDetailPath={(voucherId) => config.getVoucherDetailPath(id, voucherId)}
              />
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

PaymentRequestDetailPage.propTypes = {
  mode: PropTypes.oneOf(['personal', 'finance']),
};

export default PaymentRequestDetailPage;
